/**
 * MediaModal
 * ======================
 * Starts playback when a media dialog opens and stops it when the dialog
 * closes, so a video or embed never keeps running out of sight. Opening and
 * closing themselves are handled by the shared modal script.
 *
 *   <dialog class="modal" data-media-modal>
 *     <video data-media-modal-video>…</video>   or an <iframe> embed
 *   </dialog>
 */
class MediaModal {
  /**
   * @param {HTMLDialogElement} dialog The dialog holding the media.
   */
  constructor(dialog) {
    this.dialog = dialog;
    this.video = null;
    this.iframe = null;
    this.iframeSrc = "";

    this._onToggle = this._onToggle.bind(this);
  }

  /**
   * Watch the dialog's open state and react to it.
   */
  init() {
    if (!this.dialog) return;

    this.video = this.dialog.querySelector("[data-media-modal-video]");
    this.iframe = this.dialog.querySelector("iframe");
    if (this.iframe) this.iframeSrc = this.iframe.src;

    // <dialog> fires no open event, so the attribute is observed instead.
    this.observer = new MutationObserver(this._onToggle);
    this.observer.observe(this.dialog, { attributes: true, attributeFilter: ["open"] });

    this.dialog.addEventListener("close", () => this.stop());
  }

  /**
   * Begin playing whatever the dialog holds.
   */
  start() {
    if (this.video) {
      const started = this.video.play();
      if (started && typeof started.catch === "function") {
        // Autoplay policies may refuse; the controls are still available.
        started.catch(() => {});
      }
    }
  }

  /**
   * Halt playback. Reloading the iframe is the only way to stop a third party
   * embed from continuing to play.
   */
  stop() {
    if (this.video) {
      this.video.pause();
      this.video.currentTime = 0;
    }

    if (this.iframe) {
      this.iframe.src = this.iframeSrc;
    }
  }

  /**
   * Play or stop as the dialog opens and closes.
   */
  _onToggle() {
    if (this.dialog.open) {
      this.start();
    } else {
      this.stop();
    }
  }
}

/**
 * Boot every media dialog on the page.
 */
const initMediaModals = () => {
  document.querySelectorAll("[data-media-modal]").forEach((dialog) => {
    new MediaModal(dialog).init();
  });
};

export default initMediaModals;
export { MediaModal };
